import { useMemo } from 'react';
import { Channel as ServiceChannel } from 'services/chat';
import { useUser } from 'services/users';
import { Nullable } from '@utils/util.types';
import { Channel, ChannelType, ChatTabType } from '../types';
import { convertApiChannelToLocalModel } from '../utils/channelConversion';

interface UseChannelDataProps {
	apiChannels: ServiceChannel[];
	defaultApiChannels: ServiceChannel[];
	activeTab: ChatTabType;
	lastMessageChannel: Nullable<string>;
	lastMessageTimestamp: number;
}

export const useChannelData = ({
	apiChannels,
	defaultApiChannels,
	activeTab,
	lastMessageChannel,
	lastMessageTimestamp,
}: UseChannelDataProps) => {
	const { data: user } = useUser();

	// Convert API channels to local model channels for display
	const displayChannels = useMemo(() => {
		if (!apiChannels || apiChannels.length === 0) {
			return [];
		}
		return apiChannels.map((channel) => convertApiChannelToLocalModel(channel, user?.user_id));
	}, [apiChannels, user?.user_id]);

	// Convert default API channels to local model channels for display
	const defaultChannels = useMemo(() => {
		if (!defaultApiChannels || defaultApiChannels.length === 0) {
			return [];
		}
		return defaultApiChannels.map((channel) =>
			convertApiChannelToLocalModel(channel, user?.user_id)
		);
	}, [defaultApiChannels, user?.user_id]);

	// Filter default channels by active tab
	const filteredDefaultChannels = useMemo(() => {
		if (!defaultChannels || defaultChannels.length === 0) {
			return [];
		}

		if (activeTab === ChatTabType.Direct) {
			return defaultChannels.filter(
				(channel) =>
					channel.channelType === ChannelType.DirectMessaging ||
					channel.channelType === ChannelType.FarmerSupportMessaging ||
					channel.channelType === ChannelType.SpecialistSupportMessaging
			);
		}

		if (activeTab === ChatTabType.Groups) {
			return defaultChannels.filter(
				(channel) =>
					channel.channelType === ChannelType.GroupMessaging ||
					channel.channelType === ChannelType.ChannelMessaging ||
					channel.channelType === ChannelType.TelagriNewsFarmerChannel ||
					channel.channelType === ChannelType.TelagriNewsSpecialistChannel
			);
		}

		// no default channels on completed tab, so this is intentional
		if (activeTab === ChatTabType.Completed) {
			return [];
		}

		return [];
	}, [activeTab, defaultChannels]);

	// Custom manual sorting of channels that considers the last message channel
	const sortedChannels = useMemo(() => {
		if (!displayChannels || displayChannels.length === 0) {
			return [];
		}
		const channels = [...displayChannels];
		channels.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);

		if (lastMessageChannel) {
			const channelIndex = channels.findIndex((c) => c.id === lastMessageChannel);
			if (channelIndex > 0) {
				const channel = channels.splice(channelIndex, 1)[0];
				channels.unshift(channel);
			}
		}

		return channels;
	}, [displayChannels, lastMessageTimestamp, lastMessageChannel]);

	return {
		displayChannels,
		defaultChannels,
		filteredDefaultChannels,
		sortedChannels,
	};
}; 